import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register } from '../store/authSlice';
import { RootState, AppDispatch } from '../store';
import { FaEnvelope, FaLock, FaUser, FaSpinner, FaUserFriends, FaCheck, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { trackEvent } from '../utils/analytics';
import SEOHead from '../components/SEOHead';
import api from '../services/api';
import {
  clearContentAttribution,
  getContentAttribution,
  recordPendingContentDestination,
} from '../utils/firstPartyAnalytics';
import {
  AuthRole,
  getDefaultAuthenticatedPath,
  getSafeAuthRedirect,
} from '../utils/authRedirect';

const RegisterPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const referralCode = searchParams.get('ref') || '';
  const initialRole = searchParams.get('role') === 'employer' ? 'employer' : 'worker';
  const requestedRedirect = searchParams.get('redirect');
  const forwardRedirect = getSafeAuthRedirect(requestedRedirect);
  const utmSource = searchParams.get('utm_source') || '';
  const utmMedium = searchParams.get('utm_medium') || '';
  const utmCampaign = searchParams.get('utm_campaign') || '';
  const utmContent = searchParams.get('utm_content') || '';
  const signInUrl = forwardRedirect ? `/login?${new URLSearchParams({ redirect: forwardRedirect }).toString()}` : '/login';
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: initialRole,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const signupInteractionTracked = useRef(false);
  const registrationNavigationPending = useRef(false);
  const contentAttribution = useMemo(() => getContentAttribution(), []);

  useEffect(() => {
    recordPendingContentDestination('registration');
    trackEvent('registration_view', 'funnel', initialRole);
    if (utmSource) {
      trackEvent('utm_landing', 'acquisition', `${utmSource}/${utmMedium}/${utmCampaign}/${utmContent}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated && !registrationNavigationPending.current) {
      const redirectPath = user?.role
        ? getSafeAuthRedirect(requestedRedirect, user.role)
        : '';
      navigate(
        redirectPath || getDefaultAuthenticatedPath(user?.role),
      );
    }
  }, [isAuthenticated, navigate, requestedRedirect, user?.role]);

  // Validate referral code from URL
  useEffect(() => {
    if (referralCode) {
      api.get(`/referrals/validate/${referralCode}`)
        .then(res => {
          if (res.data.valid) {
            setReferrerName(res.data.referrerName);
          } else {
            toast.warning('Referral code is invalid or expired');
          }
        })
        .catch(() => {
          toast.warning('Could not verify referral code');
        });
    }
  }, [referralCode]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'password' && passwordValidationError) {
      setPasswordValidationError(null);
    }
    if (!signupInteractionTracked.current) {
      const interactionRole = name === 'role' ? value : formData.role;
      trackEvent('signup_started', 'funnel', interactionRole);
      signupInteractionTracked.current = true;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Real-time password validation
  const passwordChecks = useMemo(() => {
    const pw = formData.password;
    return {
      length: pw.length >= 8 && pw.length <= 128,
      hasUpper: /[A-Z]/.test(pw),
      hasLower: /[a-z]/.test(pw),
      hasNumber: /\d/.test(pw),
    };
  }, [formData.password]);

  const passwordsMatch = formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;
  const passwordsMismatch = formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword;
  const showPasswordHints = formData.password.length > 0;
  const passwordDescriptionIds = [
    showPasswordHints ? 'registration-password-guidance' : '',
    passwordValidationError ? 'registration-password-error' : '',
  ].filter(Boolean).join(' ') || undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      confirmPasswordInputRef.current?.focus();
      return;
    }
    if (formData.password.length < 8) {
      setPasswordValidationError('Password must be at least 8 characters');
      requestAnimationFrame(() => passwordInputRef.current?.focus());
      return;
    }
    if (formData.password.length > 128) {
      setPasswordValidationError('Password must be 128 characters or less');
      requestAnimationFrame(() => passwordInputRef.current?.focus());
      return;
    }
    const { confirmPassword, ...userData } = formData;
    registrationNavigationPending.current = true;
    const result = await dispatch(register({
      ...userData,
      referralCode: referralCode || undefined,
      contentSource: contentAttribution?.source,
      contentSlug: contentAttribution?.contentSlug,
    }));
    if (register.fulfilled.match(result)) {
      clearContentAttribution();
      trackEvent('sign_up', 'engagement', formData.role);
      trackEvent('signup_completed', 'funnel', formData.role);
      if (utmSource) {
        trackEvent('utm_signup', 'acquisition', `${utmSource}/${utmMedium}/${utmCampaign}/${utmContent}`);
      }
      toast.success('Account created successfully!');
      const authenticatedRole = result.payload.user?.role as AuthRole | undefined;
      const redirectPath = authenticatedRole
        ? getSafeAuthRedirect(requestedRedirect, authenticatedRole)
        : '';
      if (redirectPath) {
        navigate(redirectPath);
      } else if (authenticatedRole === 'worker') {
        navigate('/profile/setup', { state: { onboarding: true } });
      } else if (authenticatedRole === 'employer') {
        navigate('/employer/setup');
      } else {
        navigate(getDefaultAuthenticatedPath(authenticatedRole));
      }
    } else {
      registrationNavigationPending.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <SEOHead
        title="Sign Up — StageCrew | AV & Stagehand Job Marketplace"
        description="Create your free StageCrew account to browse AV technician and stagehand gigs, save preferences, and view relevant matches in StageCrew. No credit card required."
        url="https://stagecrew.app/register"
        keywords="stagehand jobs, AV technician jobs, event crew signup, live event gigs, freelance stagehand work"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Sign Up for StageCrew',
          url: 'https://stagecrew.app/register',
          description: 'Create your free StageCrew account to find or post AV and stagehand gigs.',
          isPartOf: {
            '@type': 'WebSite',
            name: 'StageCrew',
            url: 'https://stagecrew.app',
          },
        }}
      />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {formData.role === 'worker' ? 'Create a Free Worker Account' : 'Create an Employer Account'}
          </h1>
          <p className="text-gray-600 mt-2">
            {formData.role === 'worker'
              ? 'Browse live crew calls and build your basic profile—no card required.'
              : 'Post gigs and review live-event crew profiles.'}
          </p>
        </div>

        {referrerName && (
          <div className="bg-green-50 border border-green-200 rounded p-3 mb-4 flex items-center gap-2 text-green-700">
            <FaUserFriends aria-hidden="true" />
            <span className="text-sm font-medium">Referred by {referrerName}</span>
          </div>
        )}

        <div className="bg-white p-5 sm:p-8 rounded shadow">
          <form onSubmit={handleSubmit} className="space-y-5" aria-busy={loading}>
            <div data-testid="registration-name-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="registration-first-name" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <FaUser className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                  <input
                    id="registration-first-name"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    autoComplete="given-name"
                    placeholder="John"
                    className="min-h-11 w-full rounded border border-gray-500 py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="registration-last-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  id="registration-last-name"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  autoComplete="family-name"
                  placeholder="Doe"
                  className="min-h-11 w-full rounded border border-gray-500 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="registration-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                <input
                  id="registration-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="min-h-11 w-full rounded border border-gray-500 py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="registration-role" className="block text-sm font-medium text-gray-700 mb-2">
                I want to
              </label>
              <select
                id="registration-role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="min-h-11 w-full rounded border border-gray-500 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="worker">Find Work (Worker)</option>
                <option value="employer">Hire Talent (Employer)</option>
              </select>
            </div>

            <div>
              <label htmlFor="registration-password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                <input
                  id="registration-password"
                  ref={passwordInputRef}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  maxLength={128}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  aria-describedby={passwordDescriptionIds}
                  aria-invalid={passwordValidationError ? true : undefined}
                  className={`min-h-11 w-full rounded border py-2 pl-10 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    passwordValidationError
                      ? 'border-red-600'
                      : showPasswordHints
                      ? Object.values(passwordChecks).every(Boolean)
                        ? 'border-green-600'
                        : 'border-orange-600'
                      : 'border-gray-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 flex min-h-11 min-w-11 items-center justify-center rounded text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                </button>
              </div>
              {showPasswordHints && (
                <div id="registration-password-guidance" className="mt-2 space-y-1">
                  {[
                    { check: passwordChecks.length, label: 'Required — 8–128 characters' },
                    { check: passwordChecks.hasUpper, label: 'Suggested — uppercase letter' },
                    { check: passwordChecks.hasLower, label: 'Suggested — lowercase letter' },
                    { check: passwordChecks.hasNumber, label: 'Suggested — number' },
                  ].map(({ check, label }) => (
                    <div key={label} className={`flex items-center gap-1.5 text-xs ${check ? 'text-green-700' : 'text-gray-600'}`}>
                      {check ? <FaCheck className="text-green-500" aria-hidden="true" /> : <FaTimes className="text-gray-300" aria-hidden="true" />}
                      <span>{`${check ? 'Met' : 'Not met'}: ${label}`}</span>
                    </div>
                  ))}
                </div>
              )}
              {passwordValidationError && (
                <p id="registration-password-error" className="mt-1 text-xs text-red-700">
                  {passwordValidationError}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="registration-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <FaLock className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                <input
                  id="registration-confirm-password"
                  ref={confirmPasswordInputRef}
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  maxLength={128}
                  autoComplete="new-password"
                  placeholder="Confirm your password"
                  aria-describedby={formData.confirmPassword.length > 0 ? 'registration-confirm-password-feedback' : undefined}
                  aria-invalid={passwordsMismatch ? true : undefined}
                  className={`min-h-11 w-full rounded border py-2 pl-10 pr-11 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    passwordsMatch ? 'border-green-600' : passwordsMismatch ? 'border-red-600' : 'border-gray-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 flex min-h-11 min-w-11 items-center justify-center rounded text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                >
                  {showConfirmPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                </button>
              </div>
              {passwordsMismatch && (
                <p id="registration-confirm-password-feedback" className="mt-1 text-xs text-red-700 flex items-center gap-1">
                  <FaTimes className="text-red-400" aria-hidden="true" /> Passwords do not match
                </p>
              )}
              {passwordsMatch && (
                <p id="registration-confirm-password-feedback" className="mt-1 text-xs text-green-700 flex items-center gap-1">
                  <FaCheck className="text-green-500" aria-hidden="true" /> Passwords match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-blue-600 text-white py-3 rounded font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <FaSpinner className="animate-spin mr-2" aria-hidden="true" />
              ) : null}
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {showPasswordHints
              ? `${Object.values(passwordChecks).filter(Boolean).length} of 4 password checks met.`
              : ''}
          </p>
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {loading ? 'Creating your StageCrew account.' : ''}
          </p>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to={signInUrl} className="inline-flex min-h-11 items-center font-medium text-blue-600 hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
